import { RowSelectionState } from '@tanstack/react-table'
import FbButton from 'components/FbUI/FbButton'
import { ArrowLeftIcon, Download, Save } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { PATHS } from 'paths'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { DebugTable } from './DebugTable'
import { useImporterViewModel } from '../ImporterViewModel'
import { ImportWizardConfirmMatchedStep } from './ConfirmMatchedStep'
import { ImportWizardFinishMatchingStep } from './FinishMatchingStep'
import { ImportWizardDialogConfirmDeselectMatched } from './DialogConfirmDeselectMatched'
import { ImportWizardReviewPendingStep } from './ReviewPendingStep'
import { ImportWizardStartImportStep } from './StartImportStep'
import { CSVRow, InternalColumns } from '../importer-types'
import dialogConfirm from 'components/dialogConfirm'

export function ImportWizard() {
  const viewModel = useImporterViewModel()
  const navigate = useNavigate()
  const [_step, setStep] = useState<Steps>(Steps.STEP_1_CONFIRM_MATCHED_ROWS)
  const step = viewModel.areAllRowsSet ? Steps.STEP_4_START_IMPORT : _step
  const [showDebug, setShowDebug] = useState(false)

  // ================= Step 1 - Confirm Auto Matches ======================
  const [deselectedRows, setDeselectedRows] = useState<RowSelectionState>({})
  const [openConfirmDeselectModal, setOpenConfirmDeselectModal] =
    useState(false)

  const rowsToBeRemovedFromReadyState = useMemo<CSVRow[]>(() => {
    return (
      viewModel.companiesPrematched.filter(
        (row: CSVRow) => deselectedRows[row[InternalColumns.COMPANY_HASH]]
      ) ?? []
    )
  }, [deselectedRows, viewModel.companiesPrematched])

  const handleRemoveFromReadyState = useCallback(async () => {
    setLoading(true, 'Saving changes...')

    const deselectedRowCompanyHashes = rowsToBeRemovedFromReadyState.map(
      (row) => row[InternalColumns.COMPANY_HASH]
    )

    if (deselectedRowCompanyHashes.length > 0) {
      // Set ready = false
      viewModel.updateRowsReadyFromCompanyHash(
        deselectedRowCompanyHashes,
        false
      )
      for (const companyHash of deselectedRowCompanyHashes) {
        // Remove matched chain and door
        viewModel.updateMatchedChainFromCompanyHash(companyHash, null, false)
        viewModel.updateMatchedDoorFromCompanyHash(companyHash, null, false)
      }
    }

    setDeselectedRows({})

    await viewModel.handleSave()

    setLoading(false)
  }, [rowsToBeRemovedFromReadyState])

  // ================= Step 3 - Decide Unmatched Rows =======================
  const [unmatchedAction, setUnmatchedAction] = useState<'create' | 'exclude'>(
    'exclude'
  )

  async function handleConfirmMatches() {
    if (!viewModel.otherOtherType) {
      toast.error(
        'This operation cannot be performed at this time. Contact support.'
      )
      return
    }

    setLoading(true, 'Confirming Matches')

    if (viewModel.companiesLeftUnmatched.length > 0) {
      // Perform action to unmatched according to the selected action
      const hashes = viewModel.companiesLeftUnmatched.map(
        (row: CSVRow) => row[InternalColumns.COMPANY_HASH]
      )
      if (unmatchedAction == 'create') {
        viewModel.updateIsCustomCompanyFromCompanyHash(hashes, true)
        viewModel.updateCustomCompanyCategoryFromCompanyHash(
          hashes,
          viewModel.otherOtherType?.category_id,
          viewModel.otherOtherType?.value
        )
      } else if (unmatchedAction == 'exclude') {
        viewModel.updateIsCustomCompanyFromCompanyHash(hashes, false)
      }
    }

    // Set reviewed companies to ready = true
    if (viewModel.companiesPendingReview.length > 0) {
      const hashes = viewModel.companiesPendingReview
        .filter(
          (row: CSVRow) =>
            row[InternalColumns.MATCHED_CHAIN] ||
            row[InternalColumns.IS_CUSTOM_COMPANY]
        )
        .map((row: CSVRow) => row[InternalColumns.COMPANY_HASH])
      viewModel.updateRowsReadyFromCompanyHash(hashes, true)
    }

    // Asserts to guarantee consistency
    if (
      // No companies without match or not set to be custom should be ready.
      viewModel.importData?.rows.some(
        (r: CSVRow) =>
          !r[InternalColumns.MATCHED_CHAIN] &&
          !r[InternalColumns.IS_CUSTOM_COMPANY] &&
          r[InternalColumns.READY]
      )
    ) {
      setLoading(false)
      throw new Error('[IE1] Internal Error. Contact Support.')
    }
    if (
      // All companies that have a match or as set to be custom should be ready by now.
      viewModel.importData?.rows.some(
        (r: CSVRow) =>
          (r[InternalColumns.MATCHED_CHAIN] ||
            r[InternalColumns.IS_CUSTOM_COMPANY]) &&
          !r[InternalColumns.READY]
      )
    ) {
      const debugInconsistentRows = viewModel.importData?.rows.filter(
        (r: CSVRow) =>
          (r[InternalColumns.MATCHED_CHAIN] ||
            r[InternalColumns.IS_CUSTOM_COMPANY]) &&
          !r[InternalColumns.READY]
      )
      console.warn(debugInconsistentRows)
      setLoading(false)
      throw new Error('[IE2] Internal Error. Contact Support.')
    }

    // Save
    await viewModel.handleSave()

    // Call Set Companies
    await viewModel.handleSetCompanies()

    setLoading(false)
  }

  // ================= Step 4. ======================
  // Handle full import sequence
  const [loadingState, setLoadingState] = useState<{
    isLoading: boolean
    message: string
  }>({
    isLoading: false,
    message: '',
  })

  /**
   * Helper function to set loading state with a custom message
   * @example
   * setLoading(true, 'Importing companies...')
   * setLoading(true, 'Processing tags...')
   * setLoading(false) // Clear loading state
   */
  const setLoading = useCallback((isLoading: boolean, message = '') => {
    setLoadingState({ isLoading, message })
  }, [])

  const handleImport = useCallback(async () => {
    setLoading(true, 'Importing data...')

    try {
      setLoading(true, 'Importing Tags...')
      // 2. Import Tags
      if (
        viewModel.importCounts.tags.total > viewModel.importCounts.tags.imported
      ) {
        await viewModel.handleSetTags()
      }

      setLoading(true, 'Importing Deals...')
      // 3. Import Deals
      if (
        viewModel.importCounts.deals.total >
        viewModel.importCounts.deals.imported
      ) {
        const ok = await viewModel.handleSetDeals()
        if (!ok) {
          return
        }
      }

      setLoading(true, 'Importing Company Notes...')
      // 5. Import Company Notes
      if (
        viewModel.importCounts.notes.total >
        viewModel.importCounts.notes.imported
      ) {
        await viewModel.handleSetCompanyNotes()
      }

      setLoading(true, 'Importing Contacts...')
      // 4. Import Contacts
      if (
        viewModel.importCounts.contacts.total >
        viewModel.importCounts.contacts.imported
      ) {
        await viewModel.handleSetContacts()
      }
    } catch (error) {
      console.error('Import error:', error)
      toast.error('An error occurred during import')
    } finally {
      setLoading(false)
    }
  }, [
    viewModel.importData,
    setLoading,
    viewModel.handleSetCompanies,
    viewModel.handleSetTags,
    viewModel.handleSetDeals,
    viewModel.handleSetContacts,
    viewModel.handleSetCompanyNotes,
    viewModel.importCounts,
  ])

  // Individual import handlers for StartImportStep buttons
  const handleImportTags = useCallback(async () => {
    setLoading(true, 'Importing Tags...')
    try {
      await viewModel.handleSetTags()
    } catch (error) {
      console.error('Import tags error:', error)
      toast.error('An error occurred while importing tags')
    } finally {
      setLoading(false)
    }
  }, [viewModel.handleSetTags, setLoading])

  const handleImportDeals = useCallback(async () => {
    setLoading(true, 'Importing Deals...')
    try {
      await viewModel.handleSetDeals()
    } catch (error) {
      console.error('Import deals error:', error)
      toast.error('An error occurred while importing deals')
    } finally {
      setLoading(false)
    }
  }, [viewModel.handleSetDeals, setLoading])

  const handleImportContacts = useCallback(async () => {
    setLoading(true, 'Importing Contacts...')
    try {
      await viewModel.handleSetContacts()
    } catch (error) {
      console.error('Import contacts error:', error)
      toast.error('An error occurred while importing contacts')
    } finally {
      setLoading(false)
    }
  }, [viewModel.handleSetContacts, setLoading])

  const handleImportNotes = useCallback(async () => {
    setLoading(true, 'Importing Company Notes...')
    try {
      await viewModel.handleSetCompanyNotes()
    } catch (error) {
      console.error('Import notes error:', error)
      toast.error('An error occurred while importing company notes')
    } finally {
      setLoading(false)
    }
  }, [viewModel.handleSetCompanyNotes, setLoading])

  // ================= Prevent navigation when importing ======================
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (loadingState.isLoading) {
        e.preventDefault()
        e.returnValue = ''
        return ''
      }
    }

    const handlePopState = (e: PopStateEvent) => {
      if (loadingState.isLoading) {
        e.preventDefault()
        const confirmLeave = window.confirm(
          'Import is in progress. Are you sure you want to leave? This may cause data loss.'
        )
        if (!confirmLeave) {
          window.history.pushState(null, '', window.location.href)
        }
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    window.addEventListener('popstate', handlePopState)

    // Push state to enable popstate detection
    if (loadingState.isLoading) {
      window.history.pushState(null, '', window.location.href)
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      window.removeEventListener('popstate', handlePopState)
    }
  }, [loadingState.isLoading])

  // ================= Common. ======================
  function getNextStep(prevStep: Steps, fromDeselectModal?: boolean): Steps {
    switch (prevStep) {
      case Steps.STEP_1_CONFIRM_MATCHED_ROWS: {
        // skip review if all are matched
        if (
          viewModel.amountCompaniesPendingReview === 0 &&
          !fromDeselectModal
        ) {
          return Steps.STEP_3_DECIDE_UNMATCHED_ROWS
        }
        return Steps.STEP_2_REVIEW_PENDING_ROWS
      }
      case Steps.STEP_2_REVIEW_PENDING_ROWS: {
        return Steps.STEP_3_DECIDE_UNMATCHED_ROWS
      }
      case Steps.STEP_3_DECIDE_UNMATCHED_ROWS: {
        return Steps.STEP_4_START_IMPORT
      }
      default:
        return prevStep
    }
  }

  function getPrevStep(prevStep: Steps): Steps {
    switch (prevStep) {
      case Steps.STEP_4_START_IMPORT: {
        return Steps.STEP_3_DECIDE_UNMATCHED_ROWS
      }
      case Steps.STEP_3_DECIDE_UNMATCHED_ROWS: {
        // skip review if all are matched
        if (viewModel.amountCompaniesPendingReview === 0) {
          return Steps.STEP_1_CONFIRM_MATCHED_ROWS
        }
        return Steps.STEP_2_REVIEW_PENDING_ROWS
      }
      case Steps.STEP_2_REVIEW_PENDING_ROWS:
        return Steps.STEP_1_CONFIRM_MATCHED_ROWS
      default:
        return prevStep
    }
  }

  async function handleNextStep(fromDeselectModal?: boolean) {
    try {
      if (
        step === Steps.STEP_1_CONFIRM_MATCHED_ROWS &&
        Object.values(deselectedRows).some((v) => v === true) &&
        !fromDeselectModal
      ) {
        setOpenConfirmDeselectModal(true)
        return
      }

      // If moving away from handleUnmatchedRows, depending on the selection, mark unmatched rows as UGC
      if (step === Steps.STEP_3_DECIDE_UNMATCHED_ROWS) {
        let confirmationText =
          " By pressing 'OK', you confirm all the selected matches "
        if (
          viewModel.amountCompaniesLeftUnmatched > 0 &&
          unmatchedAction == 'create'
        ) {
          confirmationText +=
            'and agree that companies marked as unmatched will be created as user-generated chains.'
        }
        const confirmation = await dialogConfirm(
          confirmationText,
          'Confirm Your Matches'
        )
        if (!confirmation) {
          return
        }

        await handleConfirmMatches()
      }

      // If moving away from confirmMatchedRows, trigger import instead of navigating
      if (step === Steps.STEP_4_START_IMPORT) {
        handleImport()
      }

      setStep((prevStep) => getNextStep(prevStep, fromDeselectModal))
    } catch (e) {
      console.error(e)
      toast.error(e as string)
    }
  }

  function handlePrevStep() {
    setStep(getPrevStep)
  }

  //

  return (
    <div className="flex flex-col overflow-hidden position-relative w-full">
      {/* Loading Overlay */}
      {loadingState.isLoading && (
        <div className="absolute inset-0 bg-white bg-opacity-90 z-50 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-fb-dark-green" />
            <div className="text-lg font-semibold text-gray-900">
              {loadingState.message || 'Loading...'}
            </div>
            <div className="text-sm text-gray-600">
              Please do not close this window
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="border-b pb-2 mb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FbButton
              variant="secondary"
              onClick={() => navigate(PATHS.IMPORT_WIZARD)}
            >
              <ArrowLeftIcon size={16} />
            </FbButton>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {stepTitles[step].title}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {stepTitles[step].description}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <FbButton
              type="button"
              onClick={() => viewModel.handleSave()}
              variant="primary"
              disabled={!viewModel.hasPendingChanges}
              loading={viewModel.isSaving}
            >
              <Save size={16} />
              Save
            </FbButton>
            <FbButton
              type="button"
              onClick={() => viewModel.setShowConfigureExportModal(true)}
              variant="secondary"
            >
              <Download size={16} />
              Export
            </FbButton>
            <FbButton
              type="button"
              onClick={() => setShowDebug(!showDebug)}
              variant="secondary"
            >
              {showDebug ? 'Hide Debug' : 'Show Debug'}
            </FbButton>
          </div>
        </div>
      </div>

      {/* Content */}
      {showDebug ? (
        <DebugTable data={viewModel.importData?.rows ?? []} />
      ) : (
        <>
          {/* Step 1. */}
          <ImportWizardConfirmMatchedStep
            loading={viewModel.isFetching}
            show={
              step === Steps.STEP_1_CONFIRM_MATCHED_ROWS &&
              !loadingState.isLoading
            }
            rowSelection={deselectedRows}
            setRowSelection={setDeselectedRows}
            headers={viewModel.wizardHeaders}
            data={viewModel.companiesPrematched}
          />
          <ImportWizardDialogConfirmDeselectMatched
            show={openConfirmDeselectModal && !loadingState.isLoading}
            headers={viewModel.wizardHeaders}
            data={rowsToBeRemovedFromReadyState}
            onCancel={() => setOpenConfirmDeselectModal(false)}
            onAccept={async () => {
              await handleRemoveFromReadyState()
              setOpenConfirmDeselectModal(false)
              handleNextStep(true)
            }}
          />
          {/* Step 2. */}
          <ImportWizardReviewPendingStep
            show={
              step === Steps.STEP_2_REVIEW_PENDING_ROWS &&
              !loadingState.isLoading
            }
            data={viewModel.companiesPendingReview}
          />
          {/* Step 3. */}
          <ImportWizardFinishMatchingStep
            show={
              step === Steps.STEP_3_DECIDE_UNMATCHED_ROWS &&
              !loadingState.isLoading
            }
            data={viewModel.allCompanies}
            selectedAction={unmatchedAction}
            onActionChange={setUnmatchedAction}
          />
          {/* Step 4. */}
          <ImportWizardStartImportStep
            show={step === Steps.STEP_4_START_IMPORT || loadingState.isLoading}
            onImportTags={handleImportTags}
            onImportDeals={handleImportDeals}
            onImportContacts={handleImportContacts}
            onImportNotes={handleImportNotes}
          />
        </>
      )}

      {/* Footer with stats and buttons */}
      <div className="border-t pt-4 mt-4">
        <div className="flex items-center justify-between">
          <div className="flex gap-4 text-sm text-gray-600">
            <div>
              Matched:{' '}
              {viewModel.allCompanies.length -
                viewModel.amountCompaniesLeftUnmatched}
            </div>
            <div>Unmatched: {viewModel.amountCompaniesLeftUnmatched}</div>
          </div>

          <div className="flex gap-2">
            {step !== Steps.STEP_1_CONFIRM_MATCHED_ROWS &&
              !(
                step === Steps.STEP_4_START_IMPORT && viewModel.areAllRowsSet
              ) && (
                <FbButton
                  type="button"
                  onClick={handlePrevStep}
                  variant="secondary"
                >
                  {stepButtonTexts[step].back}
                </FbButton>
              )}
            <FbButton
              type="button"
              onClick={() => handleNextStep()}
              disabled={
                step === Steps.STEP_4_START_IMPORT && viewModel.isAllImported
              }
            >
              {stepButtonTexts[step].next}
            </FbButton>
          </div>
        </div>
      </div>
    </div>
  )
}

enum Steps {
  STEP_1_CONFIRM_MATCHED_ROWS = 1,
  STEP_2_REVIEW_PENDING_ROWS = 2,
  STEP_3_DECIDE_UNMATCHED_ROWS = 3,
  STEP_4_START_IMPORT = 4,
}

const stepTitles: Record<Steps, { title: string; description: string }> = {
  [Steps.STEP_1_CONFIRM_MATCHED_ROWS]: {
    title: 'Confirm Matches',
    description:
      'These are high-confidence matches, please review and confirm. You can also deselect matches that you do not think are correct.',
  },
  [Steps.STEP_2_REVIEW_PENDING_ROWS]: {
    title: 'Review Pending Matches',
    description: 'Review the pending data',
  },
  [Steps.STEP_3_DECIDE_UNMATCHED_ROWS]: {
    title: 'Confirm Matching',
    description:
      'Review the matching and confirm to move on to importing CRM data.',
  },
  [Steps.STEP_4_START_IMPORT]: {
    title: 'CRM Data Import Summary',
    description:
      'Review the CRM data (Tags, Deals, Contacts, Company Notes) that will be imported',
  },
}

const stepButtonTexts: Record<Steps, { back: string; next: string }> = {
  [Steps.STEP_1_CONFIRM_MATCHED_ROWS]: { back: 'Back', next: 'Next' },
  [Steps.STEP_2_REVIEW_PENDING_ROWS]: { back: 'Back', next: 'Finish Review' },
  [Steps.STEP_3_DECIDE_UNMATCHED_ROWS]: { back: 'Back', next: 'Next' },
  [Steps.STEP_4_START_IMPORT]: { back: 'Back', next: 'Start Import' },
}
